import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useGetDiskInfoQuery, useMeQuery } from '@shared/api';
import { agentHref } from '@shared/agents/labels';
import { Card, CardTitle, CardSubtitle } from '@shared/ui/Card';
import { Badge } from '@shared/ui/Badge';
import { riseIn } from '@shared/theme/animations';

const Wrap = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const Hero = styled.section`
  margin-bottom: ${({ theme }) => theme.spacing(8)};
  animation: ${riseIn} 420ms ${({ theme }) => theme.motion.easeOut};
`;

const Title = styled.h1`
  margin: 0 0 ${({ theme }) => theme.spacing(2)};
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.02em;
`;

const Lead = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
  font-size: 15px;
  max-width: 640px;
`;

const SectionLabel = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${({ theme }) => theme.spacing(4)};
`;

const AgentLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: block;
`;

const AgentName = styled.span`
  font-family: ${({ theme }) => theme.font.family};
  font-weight: 700;
  letter-spacing: -0.005em;
  & .accent {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 800;
  }
`;

const Description = styled.div`
  font-size: 13px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const AGENTS = [
  {
    code: 'ceo',
    department: 'Генеральный директор и казначейство',
    description:
      'Пульт Генерального директора: остатки по всем банковским счетам через API, платежи за прошлый день, календарь и полные карточки кредитов и лизингов, план БДДС vs подписанные КС-3 vs факт поступлений, акцепт платежей и матрица объектов.',
  },
  {
    code: 'finance',
    department: 'Финансисты и казначейство',
    description:
      'Фабрика платежей и 4-ступенчатый финконтроль, выгрузка в 1С:Клиент-Банк, зарплатный шлюз с ПК «Хронум», оборотный капитал и налоговый календарь ЕНС.',
  },
  {
    code: 'statut',
    department: 'Юристы',
    description:
      'Договоры и допсоглашения, ИИ-анализ рисков, претензионно-исковая работа, деловая переписка, проверка контрагентов, ЛНА и передача договоров в исполнение смежным службам.',
  },
  {
    code: 'smeta',
    department: 'Сметчики',
    description: 'Подготовка и проверка сметной документации, нормирование расценок.',
  },
  {
    code: 'econ',
    department: 'Экономисты',
    description: 'Управленческая отчётность: БДР, добыча ПИ, столовая — извлечение из первичных документов и сверка план/факт.',
  },
  {
    code: 'pto',
    department: 'ПТО',
    description: 'Рабочая документация с версионностью, извлечение объёмов, исполнительная документация (АОСР/АОСК), сверка объёмов, заявки/реестры и приёмочные инспекции — со ссылками на шифр и лист.',
  },
  {
    code: 'geodesy',
    department: 'Отдел геодезии',
    description: 'Исполнительные схемы по ГОСТ Р 51872-2024: сличение съёмки с РД, отклонения по типу работ, DXF/PDF, увязка с ОСНОВА.Инженер.',
  },
  {
    code: 'mto',
    department: 'Отдел снабжения',
    description: 'Материально-техническое обеспечение: ведомость материалов по РД объекта с выгрузкой в Excel, коды 1С, сверка с заявками, контроль снабжения, паспорта качества.',
  },
  {
    code: 'progress',
    department: 'Руководители проектов',
    description: 'Всё по объекту на одном экране: переписка с Заказчиком, претензии, судебные дела, прогресс и процентовка, план и факт КСП, советник ОСНОВА.Эксперт.',
  },
  {
    code: 'ksp',
    department: 'Планирование (КСП)',
    description: 'Календарно-сетевое планирование: графики производства работ, вехи, критический путь, контроль отставаний и уведомления.',
  },
  {
    code: 'logist',
    department: 'Логистика',
    description: 'Управление перевозками и маршрутами, контроль транспортных накладных, подписантов и статусов доставки.',
  },
  {
    code: 'field',
    department: 'Прорабы',
    description: 'Полевой сбор данных с площадок: конструктив и заявки на освидетельствование (автосборка АОСР с материалами и фотодоказательствами), сменные рапорты, фотофиксация, приём ТТН и аномалии. Ввод — в мобильном приложении, обзор — в вебе.',
  },
  {
    code: 'meetings',
    department: 'Совещания',
    description: 'Аудиозаписи и стенограммы строительных планёрок и совещаний, автоматическое выделение поручений и контроль сроков.',
  },
  {
    code: 'dispatcher',
    department: 'Канцелярия и маршрутизация',
    description: 'Единое окно входящей корреспонденции и документов: первичная классификация и маршрутизация по профильным службам.',
  },
  {
    code: 'kadry',
    department: 'Отдел кадров',
    description: 'Подбор персонала: заявки, поиск кандидатов на площадках, кадровый резерв и бот для кандидатов.',
  },
  {
    code: 'archivist',
    department: 'Все сотрудники',
    description:
      'Находит документы на Диске и отвечает по их содержимому — со ссылкой на файл и страницу. Отвечает только по тому, что вам открыто.',
  },
];

const AGENT_NAMES: Record<string, string> = {
  ceo: 'Гендиректор',
  finance: 'Финансист',
  statut: 'Юрист',
  smeta: 'Сметчик',
  econ: 'Экономист',
  pto: 'Инженер',
  geodesy: 'Геодезист',
  mto: 'МТО',
  progress: 'Руководитель',
  ksp: 'Планировщик',
  logist: 'Логист',
  field: 'Прораб',
  meetings: 'Совещания',
  dispatcher: 'Диспетчер',
  kadry: 'Кадры',
  archivist: 'Архивариус',
};

const CrossSectionLabel = styled(SectionLabel)`
  margin-top: ${({ theme }) => theme.spacing(10)};
`;

const AgentCardWrap = styled(Card)<{ $available: boolean }>`
  height: 100%;
  opacity: ${({ $available }) => ($available ? 1 : 0.6)};
`;

const FullHeightCard = styled(Card)`
  height: 100%;
`;

// ФИО заводят как «Фамилия Имя Отчество», здороваемся «Имя Фамилия».
function greetingName(fullName: string): string {
  const [last = '', first] = fullName.trim().split(/\s+/);
  return first ? `${first} ${last}` : last;
}

export function DashboardPage() {
  const { data: ctx } = useMeQuery();
  // Архивариус живёт в окне Диска: без Диска на сервере карточки нет, а
  // пока Диск человеку не открыт (пилот другого отдела) — «нет доступа».
  const { data: diskInfo } = useGetDiskInfoQuery();
  const accessible = new Set(
    (ctx?.agents ?? []).filter((a) => a.can_read).map((a) => a.agent_code),
  );
  const name = greetingName(ctx?.user?.full_name ?? '');
  const agents = AGENTS.filter((a) => a.code !== 'archivist' || diskInfo?.enabled);

  return (
    <Wrap>
      <Hero>
        <Title>
          Здравствуйте
          {name ? `, ${name}` : ''}
        </Title>
        <Lead>ОСНОВА — фундамент цифровых операций САТКОМ. Выберите агента для начала работы.</Lead>
      </Hero>

      <SectionLabel>Доступные агенты</SectionLabel>
      <Grid>
        {agents.map((a, i) => {
          const available =
            a.code === 'archivist'
              ? accessible.has('archivist') && !!diskInfo?.available
              : accessible.has(a.code) ||
                (a.code === 'dispatcher' && accessible.has('statut')) ||
                (a.code === 'ceo' && accessible.has('progress')) ||
                (a.code === 'finance' && (accessible.has('ceo') || accessible.has('econ')));
          const body = (
            <AgentCardWrap
              $interactive={available}
              $accent={available}
              $available={available}
              $delay={60 + i * 70}
            >
              <CardTitle>
                <AgentName>
                  ОСНОВА
                  <span className="accent">.{AGENT_NAMES[a.code] ?? a.code}</span>
                </AgentName>
                {available ? (
                  <Badge $tone="success">доступен</Badge>
                ) : (
                  <Badge $tone="neutral">нет доступа</Badge>
                )}
              </CardTitle>
              <CardSubtitle>{a.department}</CardSubtitle>
              <Description>{a.description}</Description>
            </AgentCardWrap>
          );
          if (!available) return <div key={a.code}>{body}</div>;
          return (
            <AgentLink
              key={a.code}
              to={agentHref(a.code)}
              // Окно Диска (Архивариус) вернёт «Вернуться в ОСНОВУ» на дашборд.
              state={a.code === 'archivist' ? { from: '/' } : undefined}
            >
              {body}
            </AgentLink>
          );
        })}
      </Grid>

      <CrossSectionLabel>Сквозные инструменты</CrossSectionLabel>
      <Grid>
        <AgentLink to="/requests">
          <FullHeightCard $interactive $accent $delay={80}>
            <CardTitle>
              <AgentName>
                Заявки <span className="accent">между функциями</span>
              </AgentName>
              <Badge $tone="info">новое</Badge>
            </CardTitle>
            <CardSubtitle>Все функции</CardSubtitle>
            <Description>
              Сквозной обмен заявками между службами: освидетельствование (АОСР), геодезия,
              лаборатория, НК, снабжение (МТО) и юрслужба (экспертиза договоров, претензии,
              официальные письма, доверенности, проверка контрагентов).
            </Description>
          </FullHeightCard>
        </AgentLink>
      </Grid>
    </Wrap>
  );
}
